import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useAuth from '../hooks/useAuth';
import api from '../services/api';
import {
  PREFERENCIAS_PADRAO,
  aplicarNoDocumento,
  gravarEspelho,
  lerEspelho,
  normalizarPreferencias
} from '../acessibilidadePainel/preferencias';
import { carregarVLibras, idiomaAceitaVLibras, removerVLibras } from '../acessibilidadePainel/vlibras';
import { NAMESPACE } from '../acessibilidadePainel/i18n';
import { notifyToast } from './ToastContext';
import OcultarVLibras from '../components/OcultarVLibras';

// Preferências de acessibilidade do usuário (painel da engrenagem).
//   - Fonte da verdade: o backend (/api/acessibilidade/preferencias), por
//     usuário, para valer em qualquer dispositivo.
//   - Espelho no localStorage: aplicado já no index.js, antes do primeiro
//     desenho, para não piscar o padrão e depois trocar. Vale também no login
//     e na recuperação de senha, onde ainda não há usuário.
//   - Sem preferência (o padrão): nada é aplicado. Ver acessibilidadePainel/preferencias.js.
const AcessibilidadeContext = createContext(null);
const ROTA = '/acessibilidade/preferencias';
// Falha aqui nunca derruba a sessão nem leva ao login (o interceptor do api.js).
const OPCOES_API = { skipAuthRedirect: true };

export const AcessibilidadeProvider = ({ children }) => {
  const { usuario } = useAuth() || {};
  const { i18n } = useTranslation();
  const [preferencias, setPreferencias] = useState(lerEspelho);
  // Mexeu no painel antes da resposta do servidor chegar: vale o que a pessoa fez.
  const editouRef = useRef(false);
  const usuarioId = usuario?.id;

  useEffect(() => {
    aplicarNoDocumento(preferencias);
  }, [preferencias]);

  useEffect(() => {
    if (!usuarioId) return undefined;
    let cancelado = false;
    editouRef.current = false;
    api.get(ROTA, OPCOES_API)
      .then(({ data }) => {
        if (cancelado || editouRef.current) return;
        // null = o usuário nunca mexeu: padrão, mesmo que o espelho deste
        // aparelho tenha ficado com a escolha de outra pessoa.
        const doServidor = normalizarPreferencias(data?.preferencias);
        gravarEspelho(doServidor);
        setPreferencias(doServidor);
      })
      .catch(() => { /* sem resposta: fica o espelho */ });
    return () => { cancelado = true; };
  }, [usuarioId]);

  const atuaisRef = useRef(preferencias);
  atuaisRef.current = preferencias;

  const alterar = useCallback((parcial) => {
    editouRef.current = true;
    const novas = normalizarPreferencias({ ...atuaisRef.current, ...parcial });
    atuaisRef.current = novas;
    gravarEspelho(novas);
    setPreferencias(novas);
    if (usuarioId) api.put(ROTA, { preferencias: novas }, OPCOES_API).catch(() => {});
  }, [usuarioId]);

  const restaurar = useCallback(() => alterar(PREFERENCIAS_PADRAO), [alterar]);

  const idiomaPt = idiomaAceitaVLibras(i18n.language);
  const vlibrasAtivo = preferencias.vlibras && idiomaPt;

  // O script do VLibras só é baixado com a opção ligada e o idioma em
  // português; desligar (ou trocar de idioma) tira o widget da página.
  useEffect(() => {
    if (vlibrasAtivo) carregarVLibras();
    else removerVLibras();
  }, [vlibrasAtivo]);

  // Botão "Ocultar Libras" ao lado do widget: desliga a opção (como o painel)
  // e avisa onde religar. O foco vai para o conteúdo, já que o botão some.
  const ocultarVLibras = useCallback(() => {
    alterar({ vlibras: false });
    notifyToast({ mensagem: i18n.t(`${NAMESPACE}:vlibrasHiddenNotice`), tipo: 'success', duration: 7000 });
    document.getElementById('conteudo-principal')?.focus({ preventScroll: true });
  }, [alterar, i18n]);

  const valor = useMemo(() => ({
    preferencias,
    alterar,
    restaurar,
    idiomaPt,
    vlibrasAtivo,
    ocultarVLibras
  }), [preferencias, alterar, restaurar, idiomaPt, vlibrasAtivo, ocultarVLibras]);

  return (
    <AcessibilidadeContext.Provider value={valor}>
      {children}
      {vlibrasAtivo && <OcultarVLibras aoOcultar={ocultarVLibras} />}
    </AcessibilidadeContext.Provider>
  );
};

export const useAcessibilidade = () => {
  const contexto = useContext(AcessibilidadeContext);
  if (!contexto) throw new Error('useAcessibilidade precisa do AcessibilidadeProvider');
  return contexto;
};
